import { LeagueCardv2 } from '@pickleballinc/features/src/global/cards/LeagueCardv2';
import { formatLocation } from '@pickleballinc/lib/format-location';
import { LeagueCard } from '@pickleballinc/ui/components/global/Cards/LeagueCard';
import { isAbsoluteUrl } from '@pickleballinc/ui/components/global/utils/isAbsoluteUrl';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useEffect, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { useLeagueFavorite } from '@/modules/league/hooks/useFavoriteToggle';
import { MyLeague } from '@/types/leagues.types';
import { FEATURE_FLAGS, LEAGUES_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';
import { checkIfValidUUID } from '@/utils/helpers/uuid';

export interface MyLeagueCardProps {
  league: MyLeague;
  isManageLeague: boolean;
  isManagingCard?: boolean;
  isPlayerProfileLeaguesPage?: boolean;
  hideCardHref?: boolean;
  shouldRenderFavoriteCard?: boolean;
}

export function renderLeagueCardLabels(league: MyLeague) {
  {
    if (league.IsLocked === true) {
      return 'completed';
    } else {
      if (league.IsRegClosed === 0) {
        if (parseFloat(league.Cost_Registration_Member_Cur) > 0) {
          return 'price';
        } else {
          return 'free';
        }
      } else {
        return 'registration-closed';
      }
    }
  }
}

export const MyLeagueCard = ({
  league,
  isManagingCard,
  hideCardHref = false,
  shouldRenderFavoriteCard = true,
}: MyLeagueCardProps) => {
  const { LeagueID, slug, LeagueSessionID_Cur, LeagueSessionID_Nxt } = league;
  const [pbracketsURL, setPbracketsURL] = useState('');
  const newStatus = renderLeagueCardLabels(league);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(`${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
      url.searchParams.set('mf', 'l');
      url.searchParams.set('mid', LeagueID);
      url.searchParams.set('todo', 'm');
      url.searchParams.set('rurl', window.location.href);
      setPbracketsURL(url.toString());
    }
  }, [LeagueID]);

  const shouldRedirectToPBrackets =
    (!league.SessionNumber && !league.SessionNumber_Cur) || !league.showToPublic;
  const isUUID = checkIfValidUUID(LeagueID);

  const internalHref = `${publicEnv.NEXT_PUBLIC_PL_URL}/leagues/${slug || LeagueID}/${
    LeagueSessionID_Cur
      ? `session/${LeagueSessionID_Cur}?tab=standings`
      : LeagueSessionID_Nxt
        ? `session/${LeagueSessionID_Nxt}?tab=standings`
        : ''
  }`;
  const externalHref = `${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/lgd.aspx?lid=${LeagueID}`;

  const href =
    isManagingCard && shouldRedirectToPBrackets
      ? pbracketsURL
      : isUUID
        ? internalHref
        : externalHref;

  const isExternal = (isManagingCard && shouldRedirectToPBrackets) || !isUUID;
  const rawLogo = league.LeagueLogo || league.ClubLogo;
  // Logos saved through manage are full media-service URLs; legacy ones are bare filenames.
  const logoUrl = rawLogo
    ? isAbsoluteUrl(rawLogo)
      ? rawLogo
      : `${publicEnv.NEXT_PUBLIC_PBRACKETS_CDN_URL}/uploads/clubs/${league.ClubID}/${rawLogo}`
    : LEAGUES_PLACEHOLDER_IMAGE;

  const cardContent = FEATURE_FLAGS.ENABLE_NEW_CARDS ? (
    <LeagueCardv2
      id={league.LeagueID}
      status={newStatus}
      title={league.LeagueTitle}
      location={formatLocation([league.City, league.StateAbbreviation, league.CountryAbbreviation])}
      currency={league.PaymentCurrencyAbbreviation}
      price={league.Cost_Registration_Member_Cur || '0'}
      formatTag={league.PaymentCurrencyFormatTag}
      logo={logoUrl}
      registrationCount={league.TotalRegistered || 0}
      clubTitle={league.ClubTitle}
      key={league.LeagueID}
      isLocked={league.IsLocked}
      isRegistrationClosed={league.IsRegClosed === 1}
      shouldHideStartTime
      leaguesAppBaseURL={publicEnv.NEXT_PUBLIC_PL_URL}
      useFavoriteState={useLeagueFavorite}
      showFavoriteIcon={shouldRenderFavoriteCard}
      initialIsFavorite={league.isFavorite}
    />
  ) : (
    <LeagueCard
      status={newStatus}
      title={league.LeagueTitle}
      location={formatLocation([league.City, league.StateAbbreviation, league.CountryAbbreviation])}
      currency={league.PaymentCurrencyAbbreviation}
      price={league.Cost_Registration_Member_Cur || '0'}
      formatTag={league.PaymentCurrencyFormatTag}
      logo={logoUrl}
      registrationCount={league.TotalRegistered || 0}
      clubTitle={league.ClubTitle}
      key={league.LeagueID}
      isLocked={league.IsLocked}
      isRegistrationClosed={league.IsRegClosed === 1}
      shouldHideStartTime
    />
  );

  return (
    <div key={LeagueID} className='flex w-full max-w-lg'>
      {hideCardHref ? (
        <div className='relative flex w-full hover:opacity-80'>{cardContent}</div>
      ) : isExternal ? (
        <a
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          aria-label={`Link to ${league.LeagueTitle} league on Pickleballbrackets (Opens in new tab)`}
          className='relative flex w-full hover:opacity-80'
        >
          {cardContent}
        </a>
      ) : (
        <CustomLink
          href={href}
          target='_self'
          aria-label={`Link to ${league.LeagueTitle} league`}
          className='relative flex w-full hover:opacity-80'
        >
          {cardContent}
        </CustomLink>
      )}
    </div>
  );
};
